import { API_V1, env } from '@/lib/env';
import { ApiError } from '@/lib/apiError';
import { ErrorCode, type ErrorBody, type Page, type PageMeta, type SuccessBody } from '@/types/api';

// -----------------------------------------------------------------------------
// The API client.
//
// The single place a network request is made. No component calls `fetch`.
//
// It owns six things that are wrong to duplicate per call site:
//   1. attaching the Firebase ID token AND the App Check attestation token
//   2. refreshing ONCE and retrying ONCE on AUTH_EXPIRED, then giving up
//   3. the same one-shot refresh-and-replay for a stale APP_CHECK_FAILED
//   4. a 20s timeout, composed with the caller's own abort signal
//   5. mapping every possible failure onto a typed ApiError with a stable code
//   6. sending X-Request-Id and surfacing the one that comes back
//
// Two tokens, two different questions. The ID token says WHO is calling; the
// App Check token says WHAT is calling. `Backend/src/routes/index.ts` mounts
// `verifyAppCheck()` ahead of authentication on the whole /api/v1 surface and
// `Backend/src/config/env.ts` refuses to boot production with enforcement off,
// so a request without the attestation header is a 401 in production no matter
// how good its bearer token is.
//
// Both are injected rather than imported. `lib/auth.tsx` needs the client (to
// call /auth/me) and the client needs the token - importing both ways is a
// cycle, and the indirection also makes the client trivially testable with a
// stub token provider.
// -----------------------------------------------------------------------------

type TokenProvider = {
  /** Current ID token, or null when signed out. `force` bypasses the SDK cache. */
  getToken(force?: boolean): Promise<string | null>;
  /**
   * Current App Check attestation token, or null when App Check is not
   * configured for this build (development with no site key) or when the
   * provider could not produce one. `force` bypasses the SDK cache.
   *
   * Returning null rather than throwing is deliberate: the request still goes
   * out and the API answers `APP_CHECK_FAILED`, which `describeError` already
   * renders as "Request blocked". Throwing here would surface a reCAPTCHA
   * problem as a NETWORK_ERROR on every screen at once.
   */
  getAppCheckToken(force?: boolean): Promise<string | null>;
  /** Called when a refreshed token is still rejected - the session is dead. */
  onAuthFailure(): void;
};

let tokenProvider: TokenProvider | null = null;

export function registerTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

const DEFAULT_TIMEOUT_MS = 20_000;

export type QueryParams = Record<
  string,
  string | number | boolean | null | undefined
>;

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: QueryParams;
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Skip the Authorization header. Only the health probe needs this. */
  anonymous?: boolean;
  /**
   * Sent as `Idempotency-Key` for a write that must apply once however often
   * it is retried (a coin adjustment). Reuse the same key for a retry of the
   * same submit; the API replays the first response.
   */
  idempotencyKey?: string;
};

function buildUrl(path: string, query?: QueryParams): string {
  const url = new URL(`${env.apiBaseUrl}${API_V1}${path}`);

  for (const [key, value] of Object.entries(query ?? {})) {
    // Undefined and null mean "filter not applied". Serialising them as the
    // strings "undefined"/"null" is how a cleared filter turns into a search for
    // the literal word.
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }

  return url.toString();
}

function newRequestId(): string {
  // Matches the backend's accepted shape (`/^[\w.:-]+$/`, ≤128 chars).
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Combines the caller's signal with the timeout signal.
 *
 * `AbortSignal.any` is not in every browser this may run in, so it is used when
 * present and hand-rolled when not - a component unmounting mid-request must
 * cancel it either way, otherwise a fast tab-switcher accumulates in-flight
 * requests that resolve into unmounted trees.
 */
function combineSignals(signals: AbortSignal[]): { signal: AbortSignal; cleanup: () => void } {
  if (signals.length === 1) return { signal: signals[0]!, cleanup: () => {} };

  const anyFn = (AbortSignal as unknown as { any?: (s: AbortSignal[]) => AbortSignal }).any;
  if (typeof anyFn === 'function') {
    return { signal: anyFn(signals), cleanup: () => {} };
  }

  const controller = new AbortController();
  const abort = (): void => controller.abort();
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort();
      break;
    }
    signal.addEventListener('abort', abort, { once: true });
  }

  return {
    signal: controller.signal,
    cleanup: () => {
      for (const signal of signals) signal.removeEventListener('abort', abort);
    },
  };
}

function isErrorBody(value: unknown): value is ErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { success?: unknown }).success === false &&
    typeof (value as { error?: unknown }).error === 'object' &&
    (value as { error: unknown }).error !== null
  );
}

/** Maps an HTTP status to a code, for a response that is not our envelope. */
function codeForStatus(status: number): (typeof ErrorCode)[keyof typeof ErrorCode] {
  if (status === 401) return ErrorCode.AUTH_REQUIRED;
  if (status === 403) return ErrorCode.FORBIDDEN;
  if (status === 404) return ErrorCode.RESOURCE_NOT_FOUND;
  if (status === 409) return ErrorCode.RESOURCE_CONFLICT;
  if (status === 413) return ErrorCode.PAYLOAD_TOO_LARGE;
  if (status === 415) return ErrorCode.UNSUPPORTED_MEDIA_TYPE;
  if (status === 422) return ErrorCode.VALIDATION_FAILED;
  if (status === 429) return ErrorCode.RATE_LIMITED;
  if (status === 503) return ErrorCode.SERVICE_UNAVAILABLE;
  return ErrorCode.INTERNAL_ERROR;
}

/**
 * Which of the two tokens to re-mint on a replay.
 *
 * They fail independently and cost differently - an ID token refresh is a round
 * trip to Google's token endpoint, an App Check refresh re-runs reCAPTCHA - so a
 * replay forces exactly the one the server complained about.
 */
type ForcedRefresh = { idToken: boolean; appCheck: boolean };

const NO_REFRESH: ForcedRefresh = { idToken: false, appCheck: false };

async function performRequest(
  path: string,
  options: RequestOptions,
  requestId: string,
  force: ForcedRefresh,
): Promise<Response> {
  const { method = 'GET', query, body, signal, timeoutMs = DEFAULT_TIMEOUT_MS, anonymous, idempotencyKey } = options;

  const headers = new Headers({ 'X-Request-Id': requestId });
  if (body !== undefined) headers.set('Content-Type', 'application/json');
  if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey);

  if (tokenProvider) {
    // NOT gated on `anonymous`. That flag means "this endpoint needs no
    // identity", which is a statement about authentication; App Check runs
    // ahead of authentication and covers the whole API surface, the health
    // probe included.
    const appCheckToken = await tokenProvider.getAppCheckToken(force.appCheck);
    if (appCheckToken) headers.set('X-Firebase-AppCheck', appCheckToken);

    if (!anonymous) {
      const token = await tokenProvider.getToken(force.idToken);
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }
  }

  const timeoutController = new AbortController();
  const timer = setTimeout(() => timeoutController.abort(), timeoutMs);

  const combined = combineSignals(
    signal ? [signal, timeoutController.signal] : [timeoutController.signal],
  );

  try {
    return await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: combined.signal,
      // The API authenticates with a Bearer token, not a cookie. Sending
      // credentials would widen the CORS surface for no benefit.
      credentials: 'omit',
    });
  } finally {
    clearTimeout(timer);
    combined.cleanup();
  }
}

async function parseError(response: Response, fallbackRequestId: string): Promise<ApiError> {
  const requestId = response.headers.get('X-Request-Id') ?? fallbackRequestId;

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    // A proxy or a gateway timeout answers with HTML, not our envelope.
  }

  if (isErrorBody(payload)) {
    return new ApiError({
      code: payload.error.code as (typeof ErrorCode)[keyof typeof ErrorCode],
      message: payload.error.message,
      status: response.status,
      details: payload.error.details,
      requestId: payload.error.requestId || requestId,
    });
  }

  return new ApiError({
    code: codeForStatus(response.status),
    message: `Request failed with status ${response.status}.`,
    status: response.status,
    requestId,
  });
}

function toApiError(error: unknown, requestId: string, callerAborted: boolean): ApiError {
  if (error instanceof ApiError) return error;

  if (error instanceof DOMException && error.name === 'AbortError') {
    // Two very different events share one exception. A caller-driven abort is an
    // unmount or a superseded search keystroke and must not surface as a failure;
    // a timeout is a real problem the user needs told about.
    return callerAborted
      ? new ApiError({ code: ErrorCode.CANCELLED, message: 'Request cancelled.', requestId })
      : new ApiError({
          code: ErrorCode.TIMEOUT,
          message: 'The server took too long to respond.',
          requestId,
        });
  }

  return new ApiError({
    code: ErrorCode.NETWORK_ERROR,
    message: 'Could not reach the server.',
    requestId,
  });
}

/**
 * Issues one API call and returns `data` from the envelope.
 *
 * On `AUTH_EXPIRED` it forces a token refresh and replays the request exactly
 * once. If the replay fails the same way the session is genuinely gone, so it
 * calls `onAuthFailure` and throws - retrying past that point is an infinite
 * loop against an endpoint that will never say yes.
 *
 * `APP_CHECK_FAILED` gets the same one-shot replay against a freshly minted
 * attestation token, and deliberately does NOT end the session. An attestation
 * token lasts about an hour, and a panel left open over a lunch break or on a
 * laptop that slept is the ordinary way one goes stale; signing the user out
 * for that would be a logout every time the machine wakes. If the replay is
 * refused too, the failure is real - a wrong site key, an unlisted domain, a
 * blocked reCAPTCHA - and `describeError` shows "Request blocked" rather than
 * pretending the session ended.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const result = await requestWithMeta<T>(path, options);
  return result.data;
}

export async function requestWithMeta<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; meta: Record<string, unknown> }> {
  const requestId = newRequestId();

  const attempt = async (force: ForcedRefresh): Promise<Response> =>
    performRequest(path, options, requestId, force);

  let response: Response;
  try {
    response = await attempt(NO_REFRESH);

    if (response.status === 401) {
      const firstError = await parseError(response.clone(), requestId);

      if (firstError.code === ErrorCode.AUTH_EXPIRED) {
        response = await attempt({ idToken: true, appCheck: false });

        if (response.status === 401) {
          tokenProvider?.onAuthFailure();
          throw await parseError(response, requestId);
        }
      } else if (firstError.code === ErrorCode.APP_CHECK_FAILED) {
        response = await attempt({ idToken: false, appCheck: true });

        if (response.status === 401) {
          throw await parseError(response, requestId);
        }
      }
    }
  } catch (error) {
    throw toApiError(error, requestId, options.signal?.aborted ?? false);
  }

  if (!response.ok) {
    const error = await parseError(response, requestId);

    // A 403 ACCOUNT_SUSPENDED means this admin was disabled mid-session. Keeping
    // them in the shell would show a UI full of failing requests; the correct
    // response is to end the session.
    if (error.code === ErrorCode.ACCOUNT_SUSPENDED || error.code === ErrorCode.AUTH_INVALID) {
      tokenProvider?.onAuthFailure();
    }

    throw error;
  }

  if (response.status === 204) {
    return { data: undefined as T, meta: {} };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new ApiError({
      code: ErrorCode.MALFORMED_RESPONSE,
      message: 'The server returned a response we could not read.',
      status: response.status,
      requestId: response.headers.get('X-Request-Id') ?? requestId,
    });
  }

  if (
    typeof payload !== 'object' ||
    payload === null ||
    (payload as { success?: unknown }).success !== true
  ) {
    throw new ApiError({
      code: ErrorCode.MALFORMED_RESPONSE,
      message: 'The server returned an unexpected response shape.',
      status: response.status,
      requestId: response.headers.get('X-Request-Id') ?? requestId,
    });
  }

  const body = payload as SuccessBody<T>;
  return { data: body.data, meta: body.meta ?? {} };
}

/**
 * A cursor-paginated list.
 *
 * The API returns items in `data` and the page cursor in `meta`, so this pulls
 * the two back into one object rather than making every caller reach into
 * `meta` and re-assert its shape.
 */
export async function requestPage<T>(
  path: string,
  options: RequestOptions = {},
): Promise<Page<T>> {
  const { data, meta } = await requestWithMeta<T[]>(path, options);

  const page: PageMeta = {
    hasMore: meta.hasMore === true,
    nextCursor: typeof meta.nextCursor === 'string' ? meta.nextCursor : null,
  };

  return { items: data, page };
}

export const apiClient = { request, requestWithMeta, requestPage };
